// Accepted file types for the Agent Knowledge Base document upload (Train
// step). Shared between UploadScreen.tsx (initial drop zone) and
// KBEditor.tsx (in-editor "Upload files" button) so the two entry points
// never drift out of sync.
export const ACCEPTED_KB_MIME_TYPES = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
];

export const ACCEPTED_KB_FILE_EXTENSIONS =
  '.pdf,.ppt,.pptx,.doc,.docx,.txt,.csv';

// Fallback matcher for files whose browser-reported MIME type is empty
// (common for some OS/file-manager combinations, notably .csv on macOS).
export const ACCEPTED_KB_FILE_EXTENSIONS_REGEX =
  /\.(pdf|ppt|pptx|doc|docx|txt|csv)$/i;

// Canonical MATCH names — must equal FAI sectionManifests verbatim (HRAI-2022,
// guarded by constants.test.ts). Internal keys, not UI copy; recruiter-facing
// labels live in TOPIC_DISPLAY_LABELS below.
export const KB_TOPICS = [
  'Brand Overview',
  'Positions',
  'Compensation',
  'Application Process and Timelines',
  'Working Hours',
  'Background Check',
  'Equipment Requirements',
  'Documents Required',
  'Employment Verification',
  'Payments From Applicants',
  'Tax Information Collection',
  'Technical Issues',
];

// Recruiter-facing label when it differs from the canonical name above. Pure UI
// copy — never affects matching or stored data. No entry = show the name as-is.
export const TOPIC_DISPLAY_LABELS: Record<string, string> = {
  'Application Process and Timelines': 'Application Process & Timeline',
  'Documents Required': 'Documents/ Certification/Licenses/ Physicals',
  'Employment Verification': 'I-9 Verification',
  'Payments From Applicants': 'Payments from Applicants',
  'Tax Information Collection': 'W-9 Tax Form',
};

// The recruiter-facing label for a topic (canonical name → display label,
// falling through to the name itself for custom topics).
export const topicDisplayLabel = (topic: string): string =>
  TOPIC_DISPLAY_LABELS[topic] ?? topic;

// Strips the "(Custom)" marker (spaced/repeated/case/fullwidth-paren variants,
// anywhere in the name) an upstream bootstrap may have baked in, so it is never
// shown (design: the marker must not surface). Display-only — never fed into
// matching or topic_key. Only the exact "(custom)" token matches, so real names
// survive ("Customer Support", "Team (Custom Rules)"). When the name is ONLY the
// marker, degrades to a bare word instead of re-showing "(Custom)".
export const stripCustomTag = (name: string): string => {
  const stripped = name
    .replace(/[（(]\s*custom\s*[)）]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped.length > 0) return stripped;
  return name.replace(/[（）()]/g, ' ').replace(/\s+/g, ' ').trim();
};

// The name to render for a topic at ANY display site: the recruiter-facing
// label with any "(Custom)" marker stripped so it never surfaces. Render every
// topic name through this — never the raw name — so no render site is missed.
export const displayTopic = (topic: string): string =>
  stripCustomTag(topicDisplayLabel(topic));

// A topic is custom when it isn't one of the predefined catalog names. Identity
// is the raw stored name, so this is unaffected by display stripping/labels.
export const isCustomTopic = (topic: string): boolean =>
  !KB_TOPICS.includes(topic);

// Placeholder text per topic — sourced from sectionManifests information arrays
export const TOPIC_PLACEHOLDERS: Record<string, string> = {
  'Brand Overview':
    "Provide a brief overview of the company, including its mission, industry, and key achievements. Describe the working atmosphere and the company's employee care initiatives.",
  Positions:
    'List all positions to be filled. For each position include: brief job description, hiring type (independent contractor or company payroll), approx. pay range, typical work shifts, job location (remote or on-site), and physical/education requirements. If there are many positions, provide a high-level overview of the different types.',
  Compensation:
    'Include: benefits and perks offered, whether pay is negotiable, approx. pay range for each position, and any opportunities for tips or commissions on top of the pay range.',
  'Application Process and Timelines':
    'Outline the steps from initial application to final decision. Specify any timelines if applicable, including timelines for specific stages.',
  'Working Hours':
    'Indicate whether the applicant can set their own hours or if shifts are predetermined, and list common work hours and shifts.',
  'Background Check':
    'Include: how long the background check takes, the process (including fingerprinting if applicable), where applicants can check their status, whether applicants pay for it, and whether the company hires convicted felons.',
  'Equipment Requirements':
    'List all equipment needed by the applicant. If a smartphone is required, specify the minimum iOS and Android versions. If it is a delivery driver role, specify vehicle requirements.',
  'Documents Required':
    'Include: what documents, certifications, or licenses are required; how applicants can upload them; who to contact for help; and how long document verification takes.',
  'Employment Verification':
    'Describe the employment verification process and how applicants should reach out for help if they are unable to complete it.',
  'Payments From Applicants':
    'List what applicants need to pay for (e.g. background check, delivery bags) and whether payment is required before or after the offer letter is issued.',
  'Tax Information Collection':
    'Explain why the applicant needs to provide tax information, what information they need to provide and how, and how to reach out for help if they run into any problems.',
  'Technical Issues':
    'Provide steps to resolve common issues such as: unable to login, unable to download the app, expired activation or verification links, issues receiving passcode or login credentials. Include how applicants can escalate if the issue persists.',
};

// Example candidate questions per topic — shown as tooltip in the editor
export const TOPIC_EXAMPLE_QUESTIONS: Record<string, string[]> = {
  'Brand Overview': [
    'What does your company do?',
    'What is the company culture like?',
    'How does the company support its employees?',
  ],
  Positions: [
    'What positions are available?',
    'Is this a full-time or part-time role?',
    'What are the job requirements?',
    'Is the position remote or on-site?',
  ],
  Compensation: [
    'What is the pay range for this role?',
    'Are there any benefits or perks?',
    'Is the pay negotiable?',
    'Are there opportunities for tips or commissions?',
  ],
  'Application Process and Timelines': [
    'What are the steps in the application process?',
    'How long does it take to hear back after applying?',
    'What happens after I submit my application?',
  ],
  'Working Hours': [
    'Can I set my own hours?',
    'What are the typical work shifts?',
    'Are weekends required?',
  ],
  'Background Check': [
    'How long does the background check take?',
    'What is the background check process?',
    'Where can I check the status of my background check?',
    'Will I have to pay for the background check?',
    'Does the company hire convicted felons?',
  ],
  'Equipment Requirements': [
    'What equipment do I need for this job?',
    'Do I need a smartphone? What version?',
    'Do I need my own vehicle?',
  ],
  'Documents Required': [
    'What documents do I need to provide?',
    'How do I upload my documents?',
    'Who do I contact if I have trouble uploading?',
    'How long does document verification take?',
  ],
  'Employment Verification': [
    'What is the I-9 verification process?',
    'What documents do I need for I-9?',
    'Who can I contact if I need help with verification?',
  ],
  'Payments From Applicants': [
    'Do I need to pay for anything during the application?',
    'When do I need to make the payment?',
    'What is the payment for?',
  ],
  'Tax Information Collection': [
    'Why do I need to provide tax information?',
    'What information do I need to submit?',
    'How do I submit my tax information?',
    'Who do I contact if I have issues?',
  ],
  'Technical Issues': [
    'I cannot log in to the app — what should I do?',
    'How do I download the app?',
    'My activation link has expired — what now?',
    'I am not receiving my login credentials — how do I get help?',
  ],
};
